import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../services/api';
import './style/BulkUploadWidget.css'; 

const BulkUploadWidget = ({ onClose, onRefresh, secureFetch }) => {
  const [step, setStep] = useState(1);
  const [organisations, setOrganisations] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Autocomplete states
  const [selectedOrg, setSelectedOrg] = useState('');
  const [orgSearchTerm, setOrgSearchTerm] = useState('');
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const [filesData, setFilesData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Fetch Orgs and Categories on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const orgs = await apiService.getOrganisations();
        const cats = await apiService.getCategories();
        setOrganisations(orgs);
        setCategories(cats);
      } catch (error) {
        console.error("Failed to load form data", error);
      }
    };
    fetchData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOrgDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter organisations based on search term
  const filteredOrgs = organisations.filter(org => 
    org.name.toLowerCase().includes(orgSearchTerm.toLowerCase())
  );

  const handleOrgSearchChange = (e) => {
    setOrgSearchTerm(e.target.value);
    setSelectedOrg(''); // Clear actual selection if they start typing again
    setShowOrgDropdown(true);
  };

  const handleOrgSelect = (org) => {
    setSelectedOrg(org.id);
    setOrgSearchTerm(org.name);
    setShowOrgDropdown(false);
  };

  // Handle File Selection (Max 10)
  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    if (selectedFiles.length + filesData.length > 10) {
      alert("You can only upload a maximum of 10 files at once.");
      return;
    }

    const newFilesData = selectedFiles.map(file => ({
      fileObj: file,
      name: file.name.split('.')[0], 
      category: '',
      startDate: '',
      expireDate: '',
      alertTiming: '1 Month Before', 
      alertFrequency: 'Weekly' 
    }));

    setFilesData([...filesData, ...newFilesData]);
  };

  const updateFileData = (index, field, value) => {
    const updatedFiles = [...filesData];
    updatedFiles[index][field] = value;
    setFilesData(updatedFiles);
  };

  const removeFile = (index) => {
    const updatedFiles = [...filesData];
    updatedFiles.splice(index, 1);
    setFilesData(updatedFiles);
  };

  const handleBulkUpload = async () => {
    if (filesData.length === 0) return alert("Please select at least one file.");
    
    for (let i = 0; i < filesData.length; i++) {
      if (!filesData[i].category) {
        return alert(`Please select a category for file: ${filesData[i].name}`);
      }
    }

    setIsUploading(true);
    let successCount = 0;

    for (let i = 0; i < filesData.length; i++) {
      const fileData = filesData[i];
      const formData = new FormData();
      
      // 1. Append all text fields FIRST
      formData.append('name', fileData.name);
      formData.append('organisationId', selectedOrg);
      formData.append('category', fileData.category);
      
      if (fileData.startDate) formData.append('startDate', fileData.startDate);
      
      if (fileData.expireDate) {
        let daysToSend = 30; // Default fallback
        if (fileData.alertTiming === '1 Month Before') daysToSend = 30;
        else if (fileData.alertTiming === '2 Months Before') daysToSend = 60;
        else if (fileData.alertTiming === '3 Months Before') daysToSend = 90;
        else if (fileData.alertTiming === '4 Months Before') daysToSend = 120;
        else if (fileData.alertTiming === '5 Months Before') daysToSend = 150;
        else if (!isNaN(fileData.alertTiming)) daysToSend = parseInt(fileData.alertTiming);

        formData.append('expireDate', fileData.expireDate);
        formData.append('alertTiming', daysToSend.toString()); 
        formData.append('alertFrequency', fileData.alertFrequency || 'Weekly'); 
      }

      formData.append('file', fileData.fileObj);

      try {
        await apiService.uploadDocument(formData);
        successCount++;
      } catch (error) {
        console.error(`Error uploading ${fileData.name}:`, error);
      }

      setUploadProgress(Math.round(((i + 1) / filesData.length) * 100));
    }

    setIsUploading(false);
    alert(`Successfully uploaded ${successCount} out of ${filesData.length} files.`);
    onRefresh();
    onClose();
  };


  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '800px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <h2>Bulk Upload Documents</h2>
          <button className="close-btn" onClick={onClose} disabled={isUploading}>&times;</button>
        </div>

        <div className="modal-body">
          {/* STEP 1: Select Organisation */}
          {step === 1 && (
            <div className="step-container">
              <h3>Step 1: Select Organisation</h3>
              
              <div className="form-group" ref={dropdownRef} style={{ position: 'relative' }}>
                <label>Organisation *</label>
                <input 
                  type="text" 
                  value={orgSearchTerm}
                  onChange={handleOrgSearchChange}
                  onFocus={() => setShowOrgDropdown(true)}
                  placeholder="Type to search organisation..."
                  required
                  autoComplete="off"
                />
                
                {showOrgDropdown && (
                  <ul className="autocomplete-dropdown">
                    {filteredOrgs.length > 0 ? (
                      filteredOrgs.map(org => (
                        <li key={org.id} onClick={() => handleOrgSelect(org)}>
                          {org.name}
                        </li>
                      ))
                    ) : (
                      <li className="no-results">No organisations found</li>
                    )}
                  </ul>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn-secondary" onClick={onClose}>Cancel</button>
                <button 
                  className="btn-primary" 
                  onClick={() => setStep(2)} 
                  disabled={!selectedOrg}
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: Select Files & Configure */}
          {step === 2 && (
            <div className="step-container">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3>Step 2: Configure Files ({filesData.length}/10)</h3>
                <button className="btn-secondary" onClick={() => setStep(1)} disabled={isUploading}>Back</button>
              </div>

              {filesData.length < 10 && (
                <div className="form-group">
                  <input 
                    type="file" 
                    multiple 
                    onChange={handleFileSelect} 
                    disabled={isUploading}
                  />
                </div>
              )}

              <div className="bulk-files-list">
                {filesData.map((file, index) => (
                  <div key={index} className="file-config-card" style={{ position: 'relative', padding: '15px', border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '15px' }}>
                    <button 
                      className="file-remove-btn"
                      onClick={() => removeFile(index)} 
                      disabled={isUploading}
                      style={{ position: 'absolute', top: '10px', right: '10px', background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#ef4444' }}
                    >
                      &times;
                    </button>
                    
                    {/* TOP ROW: Icon, File Name, and Category */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px', marginTop: '10px' }}>
                      <div className="file-icon-wrapper" style={{ fontSize: '32px' }}>
                        📄
                      </div>

                      <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                        <label>File Name *</label>
                        <input 
                          type="text" 
                          value={file.name} 
                          onChange={(e) => updateFileData(index, 'name', e.target.value)} 
                          disabled={isUploading}
                          style={{ width: '100%' }}
                        />
                      </div>

                      <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                        <label>Category *</label>
                        <select 
                          value={file.category} 
                          onChange={(e) => updateFileData(index, 'category', e.target.value)}
                          disabled={isUploading}
                          style={{ width: '100%' }}
                        >
                          <option value="">-- Select Category --</option>
                          {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* BOTTOM ROW: Dates and Alerts */}
                    <div className="file-config-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>Start Date</label>
                        <input 
                          type="date" 
                          value={file.startDate} 
                          onChange={(e) => updateFileData(index, 'startDate', e.target.value)} 
                          disabled={isUploading}
                          style={{ width: '100%' }}
                        />
                      </div>

                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>Expiry Date</label>
                        <input 
                          type="date" 
                          value={file.expireDate} 
                          onChange={(e) => updateFileData(index, 'expireDate', e.target.value)} 
                          disabled={isUploading}
                          style={{ width: '100%' }}
                        />
                      </div>

                      {file.expireDate && (
                        <>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>Set Expiry Alert</label>
                            <select 
                                value={file.alertTiming} 
                                onChange={(e) => updateFileData(index, 'alertTiming', e.target.value)} 
                                disabled={isUploading}
                                style={{ width: '100%' }}
                            >
                                <option value="1 Month Before">1 Month Before</option>
                                <option value="2 Months Before">2 Months Before</option>
                                <option value="3 Months Before">3 Months Before</option>
                                <option value="4 Months Before">4 Months Before</option>
                                <option value="5 Months Before">5 Months Before</option>
                            </select>
                            </div>

                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>Alert Frequency</label>
                            <select 
                              value={file.alertFrequency} 
                              onChange={(e) => updateFileData(index, 'alertFrequency', e.target.value)}
                              disabled={isUploading}
                              style={{ width: '100%' }}
                            >
                              <option value="Weekly">Weekly</option>
                              <option value="Monthly">Monthly</option>
                            </select>
                          </div>

                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>


              {isUploading && (
                <div className="progress-container">
                  <p className="progress-text">Uploading... {uploadProgress}%</p>
                  <div className="progress-bar-bg">
                    <div className="progress-bar-fill" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                </div>
              )}

              <div className="modal-footer">
                <button className="btn-secondary" onClick={onClose} disabled={isUploading}>Cancel</button>
                <button 
                  className="btn-primary" 
                  onClick={handleBulkUpload} 
                  disabled={isUploading || filesData.length === 0}
                >
                  {isUploading ? 'Uploading...' : 'Upload All Files'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadWidget;
